/**
 * In-memory capture of context that the QA Bug Report modal attaches
 * to a new bug task: console errors, uncaught exceptions, unhandled
 * promise rejections, and recent navigation. Initialized once at app
 * startup, only when DEV_TOOLS_ENABLED is true (zero overhead in prod).
 */

const MAX_ERRORS = 50;
const MAX_NAV = 20;

const consoleErrors = [];
const navigation = [];
let initialized = false;

const pushBounded = (arr, entry, max) => {
  arr.push(entry);
  if (arr.length > max) arr.splice(0, arr.length - max);
};

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toTimeString().slice(0, 8);
};

const safeStringify = (val) => {
  if (val instanceof Error) return val.stack || `${val.name}: ${val.message}`;
  if (typeof val === 'string') return val;
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

const recordConsoleError = (args) => {
  pushBounded(
    consoleErrors,
    {
      ts: Date.now(),
      message: args.map(safeStringify).join(' '),
    },
    MAX_ERRORS,
  );
};

export const initBugReporter = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Wrap console.error so we keep a circular buffer of recent errors.
  const origError = console.error.bind(console);
  console.error = (...args) => {
    recordConsoleError(args);
    origError(...args);
  };

  // Uncaught JS errors.
  window.addEventListener('error', (event) => {
    pushBounded(
      consoleErrors,
      {
        ts: Date.now(),
        message: `[uncaught] ${event.message}${event.filename ? ` (${event.filename}:${event.lineno})` : ''}`,
      },
      MAX_ERRORS,
    );
  });

  // Unhandled promise rejections.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    pushBounded(
      consoleErrors,
      {
        ts: Date.now(),
        message: `[unhandled rejection] ${safeStringify(reason)}`,
      },
      MAX_ERRORS,
    );
  });

  // Seed navigation with the initial URL.
  recordNavigation(window.location.href);
};

export const recordNavigation = (url) => {
  if (!url) return;
  // De-dupe consecutive identical urls (React StrictMode can double-fire).
  const last = navigation[navigation.length - 1];
  if (last && last.url === url) return;
  pushBounded(navigation, { ts: Date.now(), url }, MAX_NAV);
};

/**
 * Snapshot of the QA bug-report context.
 * Returned as a plain JSON-serializable object — the modal uploads this as
 * `bug-context.json` (an attachment on the task) so triagers can read it
 * cleanly instead of grepping the description.
 */
export const getBugReportContext = ({ user, extra } = {}) => {
  const now = new Date();
  return {
    capturedAt: now.toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    reporter: user
      ? {
          id: user.id ?? null,
          fullName: user.fullName ?? null,
          email: user.email ?? null,
          role: user.role ?? null,
          organizationId: user.organizationId ?? null,
        }
      : null,
    page: {
      url: typeof window !== 'undefined' ? window.location.href : null,
      pathname: typeof window !== 'undefined' ? window.location.pathname : null,
      referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    },
    browser: {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      language: typeof navigator !== 'undefined' ? navigator.language : null,
      platform: typeof navigator !== 'undefined' ? navigator.platform : null,
      viewport:
        typeof window !== 'undefined'
          ? { width: window.innerWidth, height: window.innerHeight }
          : null,
      devicePixelRatio:
        typeof window !== 'undefined' ? window.devicePixelRatio : null,
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    },
    consoleErrors: consoleErrors.slice(-20).map(e => ({
      ts: new Date(e.ts).toISOString(),
      time: formatTime(e.ts),
      message: e.message,
    })),
    navigation: navigation.slice(-10).map(n => ({
      ts: new Date(n.ts).toISOString(),
      time: formatTime(n.ts),
      url: n.url,
    })),
    ...(extra ? { extra } : {}),
  };
};
